import { useReducer, useEffect } from 'react';
import { CalculatedResults, calculateResults } from '../utils/calculations';
import { Question, getRawQuestionsData, initializeQuestions } from '../utils/questionService';
import { loadResults } from '../utils/storage';
import { normalizeSessionQuestions } from '../utils/normalizeSessionQuestions';
import { ResultService, TestResult } from '../services/resultService';
import { logger } from '../utils/logger';
import { Errors, safeExecuteAsync } from '../utils/errorHandling';

// --- 1. State and Reducer ---
type Status = 'loading' | 'success' | 'error';

interface ResultsState {
  status: Status;
  results: (TestResult & CalculatedResults) | null;
  unansweredQuestions: Question[];
  allQuestions: Question[];
  isSaving: boolean;
  error: Error | null;
  completionMessage: string | null;
  id: string | null;
}

const initialState: ResultsState = {
  status: 'loading',
  results: null,
  unansweredQuestions: [],
  allQuestions: [],
  isSaving: false,
  error: null,
  completionMessage: null,
  id: null,
};

function resultsReducer(state: ResultsState, action: any): ResultsState {
  switch (action.type) {
    case 'FETCH_START':
      return { ...initialState, id: action.payload.id };
    case 'FETCH_SUCCESS':
      return {
        ...state,
        status: 'success',
        results: action.payload.results,
        unansweredQuestions: action.payload.unansweredQuestions,
        allQuestions: action.payload.allQuestions, // теперь это только requiredQuestions!
        id: action.payload.id || state.id,
        error: null,
      };
    case 'FETCH_ERROR':
      return { ...state, status: 'error', error: action.payload };
    case 'UPDATE_START':
      return { ...state, isSaving: true, error: null };
    case 'UPDATE_SUCCESS':
      // Пересчитываем unansweredQuestions только по state.allQuestions (теперь это requiredQuestions)
      const answeredIds = new Set(action.payload.updatedResults.sessionQuestions.map((q: { id: string }) => q.id));
      const remainingQuestions = state.allQuestions.filter(q => !answeredIds.has(q.id));

      return {
        ...state,
        isSaving: false,
        results: action.payload.updatedResults,
        unansweredQuestions: remainingQuestions,
        completionMessage: remainingQuestions.length === 0
          ? 'Все дополнительные вопросы отвечены. Результаты обновлены.'
          : null,
        error: null,
      };
    case 'UPDATE_ERROR':
      return { ...state, isSaving: false, error: action.payload };
    default:
      return state;
  }
}

// --- 2. Data Fetching Logic ---
async function loadAndProcessResults(id: string) {
  logger.info('Starting to load and process results', { module: 'USE_RESULTS', function: 'loadAndProcessResults', step: 'start', id });
  
  await initializeQuestions();
  const allQuestions = getRawQuestionsData();
  
  logger.debugData('allQuestions', allQuestions, { module: 'USE_RESULTS', function: 'loadAndProcessResults', step: 'allQuestions' });
  
  if (!allQuestions || allQuestions.length === 0) {
    throw Errors.QUESTIONS_NOT_INITIALIZED({ id, allQuestionsCount: allQuestions?.length });
  }

  let rawData: TestResult | null = null;
  try {
    rawData = await loadResults(id, {});
    logger.debugData('rawData from Firestore', rawData, { module: 'USE_RESULTS', function: 'loadAndProcessResults', step: 'rawData' });
    
    if (!rawData) {
      throw Errors.DOCUMENT_NOT_FOUND(id, { id });
    }
  } catch (e) {
    logger.error('Error loading results from Firestore', { module: 'USE_RESULTS', function: 'loadAndProcessResults', step: 'loadResults' }, e);
    throw e;
  }

  // --- Теперь requiredQuestions — это все не disabled вопросы из базы ---
  const requiredQuestions = allQuestions.filter(q => !q.disabled);

  // --- После получения результата из Firestore ---
  logger.debugData('rawData after loadResults', rawData, { module: 'USE_RESULTS', function: 'loadAndProcessResults', step: 'afterLoadResults' });
  
  if (typeof window !== 'undefined') {
    (window as any)._psyco_debug_log = (window as any)._psyco_debug_log || [];
    (window as any)._psyco_debug_log.push({ step: 'normalizeSessionQuestions:rawData', rawData });
  }
  
  let normalized: { sessionQuestions: any[] } | null = null;
  try {
    logger.debugData('normalizeSessionQuestions input', rawData, { module: 'USE_RESULTS', function: 'loadAndProcessResults', step: 'normalizeSessionQuestions:input' });
    normalized = { sessionQuestions: normalizeSessionQuestions(rawData) };
    
    // Явное логирование sessionQuestions
    logger.debugSessionQuestions(normalized.sessionQuestions, { module: 'USE_RESULTS', function: 'loadAndProcessResults', step: 'sessionQuestions from Firestore' });
    
    logger.debugData('normalizeSessionQuestions output', normalized, { module: 'USE_RESULTS', function: 'loadAndProcessResults', step: 'normalizeSessionQuestions:output' });
    
    if (!normalized) {
      logger.error('normalizeSessionQuestions returned null/undefined', { module: 'USE_RESULTS', function: 'loadAndProcessResults', step: 'normalizeSessionQuestions:output_is_null' }, { rawData });
      throw Errors.NORMALIZATION_FAILED({ rawData });
    }
  } catch (err) {
    logger.error('Error in normalizeSessionQuestions', { module: 'USE_RESULTS', function: 'loadAndProcessResults', step: 'normalizeSessionQuestions:error' }, err);
    throw err;
  }
  
  let calculated: CalculatedResults | null = null;
  try {
    logger.debugData('calculateResults input', normalized, { module: 'USE_RESULTS', function: 'loadAndProcessResults', step: 'calculateResults:input' });
    calculated = await calculateResults(normalized);
    logger.debugData('calculateResults output', calculated, { module: 'USE_RESULTS', function: 'loadAndProcessResults', step: 'calculateResults:output' });
  } catch (err) {
    logger.error('Error in calculateResults', { module: 'USE_RESULTS', function: 'loadAndProcessResults', step: 'calculateResults:exception' }, err);
    throw err;
  }
  
  if (!calculated) {
    logger.error('calculateResults returned null/undefined', { module: 'USE_RESULTS', function: 'loadAndProcessResults', step: 'calculateResults:empty' }, { normalized });
    throw Errors.CALCULATION_FAILED({ normalized });
  }

  // --- Формируем unansweredQuestions только из обязательных (не disabled) ---
  const answeredIds = getAllAnsweredIds(normalized);
  logger.debugData('answeredIds', Array.from(answeredIds), { module: 'USE_RESULTS', function: 'loadAndProcessResults', step: 'answeredIds' });
  
  let unansweredQuestions: Question[];
  if (!normalized.sessionQuestions || normalized.sessionQuestions.length === 0) {
    // Fallback: если sessionQuestions пустой, все вопросы считаются неотвеченными
    unansweredQuestions = requiredQuestions;
  } else {
    unansweredQuestions = requiredQuestions.filter(q => !answeredIds.has(q.id));
  }
  
  logger.debugData('unansweredQuestions', unansweredQuestions.map(q => q.id), { module: 'USE_RESULTS', function: 'loadAndProcessResults', step: 'unansweredQuestions' });

  return { allQuestions: requiredQuestions, unansweredQuestions, results: { ...rawData, ...calculated } };
}

// --- Вспомогательная функция для честного union id с ответами ---
export function getAllAnsweredIds(results: { sessionQuestions?: { id: string; answer: unknown }[] }): Set<string> {
  const ids = new Set<string>();
  if (Array.isArray(results?.sessionQuestions)) {
    results.sessionQuestions.forEach(q => q && q.id && q.answer != null && q.answer !== '' && ids.add(q.id));
  }
  return ids;
}

// --- 3. The Custom Hook ---
export function useResults(id: string | undefined) {
  const [state, dispatch] = useReducer(resultsReducer, initialState);

  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;
      
      const fetchResults = async () => {
        dispatch({ type: 'FETCH_START', payload: { id } });
        try {
          logger.info('Starting to load and process results for ID', { module: 'USE_RESULTS', function: 'useResults', step: 'fetchStart' }, { id });
          
          const { results, unansweredQuestions, allQuestions } = await loadAndProcessResults(id);
          
          logger.info('Results loaded successfully', { module: 'USE_RESULTS', function: 'useResults', step: 'fetchSuccess' }, {
            hasResults: !!results,
            hasScores: results?.scores ? Object.keys(results.scores).length : 0,
            hasCluster: !!results?.cluster,
            unansweredQuestions: unansweredQuestions.length,
            allQuestions: allQuestions.length
          });
          
          if (results?.scores) {
            logger.debugScores(results.scores, { module: 'USE_RESULTS', function: 'useResults', step: 'scoresAvailable' });
          }
          
          const payload = { results, unansweredQuestions, allQuestions, id };
          dispatch({
            type: 'FETCH_SUCCESS',
            payload,
          });
          
          logger.info('FETCH_SUCCESS dispatched', { module: 'USE_RESULTS', function: 'useResults', step: 'fetchSuccess' }, payload);
        } catch (err) {
          logger.error('Error in fetchResults', { module: 'USE_RESULTS', function: 'useResults', step: 'fetchError' }, err);
          dispatch({ type: 'FETCH_ERROR', payload: err });
        }
      };
      await fetchResults();
    };
    
    fetchData();
  }, [id]);

  const handleUpdate = async (newResponse: { id: string; answer: unknown; text: string; }) => {
    logger.info('Starting handleUpdate', { module: 'USE_RESULTS', function: 'handleUpdate', step: 'start' }, newResponse);
    
    if (!state.results || !state.id) {
      logger.error('No results or id available for handleUpdate', { module: 'USE_RESULTS', function: 'handleUpdate', step: 'validation' }, { state });
      return;
    }

    dispatch({ type: 'UPDATE_START' });
    
    // --- ГАРАНТИЯ: всегда массив sessionQuestions ---
    // Собираем union всех уникальных ответов только из sessionQuestions
    const prevAnswersMap = new Map<string, { id: string; answer: unknown; text?: string }>();
    if (Array.isArray(state.results.sessionQuestions)) {
      for (const q of state.results.sessionQuestions) {
        if (q && q.id) prevAnswersMap.set(q.id, q);
      }
    }
    
    // Новый ответ (перезаписывает старый, если совпадает id)
    if (newResponse && newResponse.id) {
      prevAnswersMap.set(newResponse.id, newResponse);
    }
    
    const updatedSessionQuestions = Array.from(prevAnswersMap.values());
    logger.debugSessionQuestions(updatedSessionQuestions, { module: 'USE_RESULTS', function: 'handleUpdate', step: 'updatedSessionQuestions' });
    
    const newCalculatedResults = await calculateResults(updatedSessionQuestions.map(q => ({ id: q.id, answer: q.answer, text: q.text })));
    logger.debugData('newCalculatedResults', newCalculatedResults, { module: 'USE_RESULTS', function: 'handleUpdate', step: 'newCalculatedResults' });

    const cleanResults = { ...state.results };

    const updatedFinalResults: TestResult & CalculatedResults = {
      ...cleanResults,
      ...newCalculatedResults,
      sessionQuestions: updatedSessionQuestions,
      timestamp: new Date().toISOString(),
      id: state.id!,
    };

    logger.debugData('updatedFinalResults', updatedFinalResults, { module: 'USE_RESULTS', function: 'handleUpdate', step: 'updatedFinalResults' });

    // --- answeredIds только по id ---
    const answeredIds = getAllAnsweredIds({ sessionQuestions: updatedSessionQuestions });
    const remainingQuestions = state.allQuestions.filter(q => !answeredIds.has(q.id));

    // --- Сохраняем только если все вопросы отвечены ---
    if (remainingQuestions.length === 0) {
      await ResultService.saveResult(updatedFinalResults);
    }

    dispatch({ type: 'UPDATE_SUCCESS', payload: { updatedResults: updatedFinalResults } });
    logger.info('UPDATE_SUCCESS dispatched', { module: 'USE_RESULTS', function: 'handleUpdate', step: 'success' }, { 
      isSaving: false, 
      unansweredQuestions: remainingQuestions 
    });
  };

  // --- DEBUG: логируем всё, что возвращаем наружу ---
  if (typeof window !== 'undefined') {
    logger.debugData('useResults return state', {
      unansweredQuestions: state.unansweredQuestions,
      allQuestions: state.allQuestions,
      results: state.results,
      id: state.id,
      isSaving: state.isSaving,
      error: state.error,
      completionMessage: state.completionMessage
    }, { module: 'USE_RESULTS', function: 'useResults', step: 'return' });
  }
  
  return { ...state, handleUpdate };
}
